
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from '@/context/LanguageContext';
import { FaGoogle } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import { useResendRateLimit } from "@/hooks/useResendRateLimit";
import MainLayout from "@/components/layout/MainLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const Signup = () => {
  // 表單狀態
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isOAuthSignup, setIsOAuthSignup] = useState(false);
  const verificationRateLimit = useResendRateLimit('verification');

  // 初始化
  useEffect(() => {
    checkSession();
    
    // 從 URL 參數獲取邀請碼
    const urlInviteCode = searchParams.get('invite') || searchParams.get('ref');
    if (urlInviteCode) {
      setInviteCode(urlInviteCode);
    }

    // 設定認證狀態監聽器
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("認證狀態變更:", event, !!session);
      if (session && isOAuthSignup) {
        checkProfileAndRedirect(session.user.id);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [navigate, isOAuthSignup]);

  // 檢查現有會話
  const checkSession = async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      console.log("使用者已登入，檢查個人資料");
      checkProfileAndRedirect(data.session.user.id);
    }
  };

  // 檢查個人資料並重定向
  const checkProfileAndRedirect = async (userId: string) => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('full_name, nickname, country, is_verified')
        .eq('id', userId)
        .single();
      
      console.log("個人資料檢查:", profileData, error);
      
      if (error || !profileData?.full_name || !profileData?.nickname || !profileData?.country) {
        console.log("個人資料不完整，重定向到完成註冊頁面");
        navigate("/signup-complete");
      } else {
        console.log("個人資料完整，重定向到首頁");
        navigate("/");
      }
    } catch (error) {
      console.error("檢查個人資料錯誤:", error);
      navigate("/signup-complete");
    }
  };

  // 驗證邀請碼
  const validateInviteCode = async (code: string) => {
    if (!code) return null;
    
    try {
      const { data: referrer } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('invite_code', code.toUpperCase())
        .single();
      
      return referrer;
    } catch (error) {
      console.error("驗證邀請碼錯誤:", error);
      return null;
    }
  };

  // 處理註冊
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 檢查服務條款同意
    if (!agreedToTerms) {
      toast({
        variant: "destructive",
        title: t('auth.signupFailed'),
        description: t('auth.agreeToTermsRequired')
      });
      return;
    }
    
    // 檢查密碼確認
    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: t('auth.signupFailed'),
        description: t('auth.passwordsDontMatch')
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // 如果有邀請碼，先驗證
      let referrerId = null;
      if (inviteCode) {
        const referrer = await validateInviteCode(inviteCode);
        if (!referrer) {
          toast({
            variant: "destructive",
            title: "邀請碼無效",
            description: "請檢查邀請碼是否正確"
          });
          setIsLoading(false);
          return;
        }
        referrerId = referrer.id;
        
        toast({
          title: "邀請碼驗證成功",
          description: `已確認邀請人：${referrer.full_name}`
        });
      }

      // 執行註冊
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            referrer_id: referrerId
          }
        }
      });

      if (error) {
        // 根據錯誤訊息判斷帳號是否已存在
        if (error.message.includes('already registered') || 
            error.message.includes('User already exists') || 
            error.message.includes('User already registered')) {
          toast({
            variant: "destructive",
            title: t('auth.signupFailed'),
            description: t('auth.accountAlreadyRegistered') || '此帳號已經註冊過請至登入頁面'
          });
        } else {
          toast({
            variant: "destructive",
            title: t('auth.signupFailed'),
            description: error.message
          });
        }
        setIsLoading(false);
        return;
      }

      // 註冊成功，導向驗證通知頁面
      navigate("/signup-verify-notice", {
        state: { email }
      });
      
    } catch (error: any) {
      console.error("註冊錯誤:", error);
      toast({
        variant: "destructive",
        title: t('auth.signupFailed'),
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 處理 Google 註冊
  const handleGoogleSignup = async () => {
    if (!agreedToTerms) {
      toast({
        variant: "destructive",
        title: t('auth.signupFailed'),
        description: t('auth.agreeToTermsRequired')
      });
      return;
    }
    
    setIsOAuthSignup(true);
    
    try {
      const redirectUrl = new URL(`${window.location.origin}/signup-complete`);
      if (inviteCode) {
        redirectUrl.searchParams.set('invite', inviteCode);
      }
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl.toString()
        }
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('auth.signupFailed'),
        description: error.message
      });
    }
  };

  return (
    <MainLayout>
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-lg">
          {/* 頁面標題 */}
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight">{t('auth.createAccount')}</h2>
            <p className="mt-2 text-sm text-gray-600">
              {t('auth.alreadyHaveAccount')} {" "}
              <a href="/login" className="text-primary-purple hover:underline">
                {t('auth.signIn')}
              </a>
            </p>
          </div>

          {/* 驗證提醒 */}
          {showVerificationAlert && (
            <Alert className="bg-green-50 border-green-200">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {t('auth.checkYourEmail')}
              </AlertDescription>
            </Alert>
          )}

          {/* 註冊表單 */}
          <form onSubmit={handleSignup} className="mt-8 space-y-6">
            <div className="space-y-4">
              {/* 電子郵件 */}
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  required 
                />
              </div>
              
              {/* 密碼 */}
              <div>
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={e => setPassword(e.target.value)} 
                  required 
                />
              </div>
              
              {/* 確認密碼 */}
              <div>
                <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                <Input 
                  id="confirmPassword" 
                  type="password" 
                  value={confirmPassword} 
                  onChange={e => setConfirmPassword(e.target.value)} 
                  required 
                />
              </div>
              
              {/* 邀請碼 */}
              <div>
                <Label htmlFor="inviteCode">邀請碼 (選填)</Label>
                <Input 
                  id="inviteCode" 
                  type="text" 
                  value={inviteCode} 
                  onChange={e => setInviteCode(e.target.value.toUpperCase())} 
                  placeholder="請輸入朋友的邀請碼"
                  className="uppercase"
                />
                <p className="text-sm text-gray-500 mt-1">
                  填入朋友的邀請碼，當您完成投稿後，朋友將獲得邀請獎勵
                </p>
              </div>
              
              {/* 服務條款同意 */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="terms" 
                  checked={agreedToTerms} 
                  onCheckedChange={checked => setAgreedToTerms(checked as boolean)} 
                />
                <label htmlFor="terms" className="text-sm text-gray-600 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('auth.agreeToTerms')}{" "}
                  <a href="/terms" className="text-primary hover:underline" target="_blank">
                    {t('auth.terms')}
                  </a>{" "}
                  {t('auth.and')}{" "}
                  <a href="/privacy" className="text-primary hover:underline" target="_blank">
                    {t('auth.privacy')}
                  </a>
                </label>
              </div>
            </div>

            {/* 註冊按鈕 */}
            <Button 
              type="submit" 
              disabled={isLoading} 
              className="w-full bg-orange-500 hover:bg-orange-400"
            >
              {isLoading ? t('auth.signingUp') : t('common.signup')}
            </Button>
          </form>

          {/* 分隔線 */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">{t('auth.orSignupWith')}</span>
            </div>
          </div>

          {/* Google 註冊 */}
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleGoogleSignup} 
            className="w-full"
          >
            <FaGoogle className="mr-2" />
            Google
          </Button>
        </div>
      </div>
    </MainLayout>
  );
};

export default Signup;
